import { ConditionType, conditionTypeToString, InsightCondition } from '@api';
import { WORKING_LIST_ID } from '@terminal/config/constants';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useWorkingList } from '@terminal/hooks/use-working-list/useWorkingList';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { createScenarioRequest } from '@terminal/react-query-hooks/scenarios/useScenarios.utils';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { getWatchlistEntitiesTags } from '@terminal/stores/use-watchlist/useWatchlist.utils';
import { SelectedAssetItem } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/RunScenario';
import { useScenarioAssistant } from '@terminal/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { SCENARIO_ASSET_FILTER_KEY } from '@terminal/widgets/scenario/hooks/use-scenario-store/filter-utils';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { mapAssistantConditionsToConditions } from '@terminal/widgets/scenario/utils/scenario-conditions/scenario-conditions';
import { t } from 'i18next';
import { useNavigate } from 'react-router';

interface UseScenarioInsightsProps {
  entityTag: string;
  conditions: InsightCondition[];
}

export const useScenarioFromInsights = ({
  entityTag,
  conditions,
}: UseScenarioInsightsProps) => {
  const navigate = useNavigate();
  const { saveWorkingList } = useWorkingList();
  const { trackScenarioOpen } = usePosthogEvents();
  const { addFilterOption: addScenarioFilterOption } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });
  const { resetActiveFilterOptions: resetActiveConditionFilterOptions } =
    useFilterActions({
      store: useScenarioStore,
      group: 'SCENARIO_CONDITIONS',
    });
  const { resetConditions, manuallySetCustomConditions } = useScenarioAssistant(
    state => ({
      resetConditions: state.resetConditions,
      manuallySetCustomConditions: state.manuallySetCustomConditions,
    })
  );

  const { data: competitorsData } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const onRunScenario = async (selectedAssetsItem: SelectedAssetItem) => {
    const workingListFilterOption = {
      key: WORKING_LIST_ID,
      name: t('common:filters.workingList'),
      value: {
        workingList: WORKING_LIST_ID,
      },
    };
    let scenarioTags: string[] = [];

    if (selectedAssetsItem.assetFilterValueKey === WORKING_LIST_ID) {
      scenarioTags = competitorsData?.map(c => c.company) ?? [];
    } else {
      const lists = useWatchlist.getState().lists;
      scenarioTags = getWatchlistEntitiesTags(lists, selectedAssetsItem.id);
    }

    if (!scenarioTags.some(tag => tag === entityTag)) {
      scenarioTags.push(entityTag);
    }

    saveWorkingList(scenarioTags);
    addScenarioFilterOption({
      filterKey: SCENARIO_ASSET_FILTER_KEY,
      option: workingListFilterOption,
    });

    const scenarioConditions = conditions.map(condition => ({
      snake: condition.snake,
      condition: conditionTypeToString[condition.condition] as ConditionType,
      value: Number(condition.value.toFixed(2)),
      label: '',
      entity: entityTag,
      duration: condition.duration,
      duration_unit: condition.duration_unit,
    }));
    resetActiveConditionFilterOptions();
    resetConditions();
    await manuallySetCustomConditions(scenarioConditions);

    const mappedConditions = mapAssistantConditionsToConditions(
      useScenarioAssistant.getState().conditions.filter(c => c.active)
    );
    const { setPendingAutoRunRequest, setScenarioPageEntityTags } =
      useScenarioStore.getState();

    setScenarioPageEntityTags(scenarioTags);

    if (mappedConditions.length && scenarioTags.length) {
      setPendingAutoRunRequest(
        createScenarioRequest({
          conditions: mappedConditions,
          entityTags: scenarioTags,
        })
      );
    }

    trackScenarioOpen(POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER);
    navigate(appPaths.scenario);
  };

  return {
    onRunScenario,
  };
};
